import {otsBaseApi} from "../../ots-api";
import {
  SiteAssignment,
  SiteAssignmentEntity,
  SiteAssignmentTemplate,
  siteAssignmentTypes
} from "../../../model/site-assignment";

type GetBySiteArg = {
  regionId: string;
  siteId: string;
  category: string;
};

const ALL_TAG_TYPES = [
  "SiteAssignments",
  "PickupSiteSiteAssignments",
  "DeliverySiteSiteAssignments",
  "CompanySiteAssignments"
] as const;

type TagType = typeof ALL_TAG_TYPES[number];

const taggedBaseApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: ALL_TAG_TYPES
});

const siteAssignmentApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllSiteAssignments: builder.query<SiteAssignment[], string>({
      query: (regionId) => ({
        url: `/site-assignments/all`,
        method: "GET",
        params: {regionId: regionId}
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(
              ({id}) => ({type: "SiteAssignments", id} as const)
            ),
            ...result.filter(a => a.type === siteAssignmentTypes.pickupSite.code).map(
              ({siteId, category}) => ({type: "PickupSiteSiteAssignments", id: siteId + "~" + category} as const)
            ),
            ...result.filter(a => a.type === siteAssignmentTypes.deliverySite.code).map(
              ({siteId, category}) => ({type: "DeliverySiteSiteAssignments", id: siteId + "~" + category} as const)
            ),
            ...result.filter(a => a.type === siteAssignmentTypes.company.code).map(
              ({siteId, category}) => ({type: "CompanySiteAssignments", id: siteId + "~" + category} as const)
            ),
            {type: "SiteAssignments", id: "LIST"}
          ];
        } else {
          return [
            {type: "SiteAssignments", id: "LIST"},
            {type: "PickupSiteSiteAssignments"},
            {type: "DeliverySiteSiteAssignments"},
            {type: "CompanySiteAssignments"},
          ];
        }
      }
    }),
    getSiteAssignment: builder.query<SiteAssignment, string>({
      query: (id) => ({
        url: `/site-assignments/${id}`,
        method: "GET"
      }),
      providesTags: (result, error, arg) => {
        const tags : { type: TagType, id?: string }[] = []

        if (result) {
          tags.push({type: "SiteAssignments", id: result.id})
          if (result.type === siteAssignmentTypes.pickupSite.code) {
            tags.push({type: "PickupSiteSiteAssignments", id: result.siteId + "~" + result.category})
          } else if (result.type === siteAssignmentTypes.deliverySite.code) {
            tags.push({type: "DeliverySiteSiteAssignments", id: result.siteId + "~" + result.category})
          } else if (result.type === siteAssignmentTypes.company.code) {
            tags.push({type: "CompanySiteAssignments", id: result.siteId + "~" + result.category})
          }
        }

        return tags
      }
    }),
    getCompanySiteAssignmentBySite: builder.query<
      SiteAssignment | undefined,
      GetBySiteArg
    >({
      query: (arg) => ({
        url: `/site-assignments/${arg.regionId}/${arg.siteId}/${arg.category}/company`,
        method: "GET"
      }),
      providesTags: (result, error, arg) =>
        result
          ? [
            {type: "SiteAssignments", id: result.id},
            {type: "CompanySiteAssignments", id: result.siteId + "~" + result.category}
          ]
          : [
            {type: "CompanySiteAssignments", id: arg.siteId + "~" + arg.category}
          ]
    }),
    getPickupSiteSiteAssignmentBySite: builder.query<
      SiteAssignment | undefined,
      GetBySiteArg
    >({
      query: ({regionId, siteId, category}) => ({
        url: `/site-assignments/${regionId}/${siteId}/${category}/pickup-site`,
        method: "GET"
      }),
      providesTags: (result, error, arg) =>
        result
          ? [
            {type: "SiteAssignments", id: result.id},
            {type: "PickupSiteSiteAssignments", id: result.siteId + "~" + result.category}
          ]
          : [
            {type: "PickupSiteSiteAssignments", id: arg.siteId + "~" + arg.category}
          ]
    }),
    getDeliverySiteSiteAssignmentBySite: builder.query<
      SiteAssignment | undefined,
      GetBySiteArg
    >({
      query: ({regionId, siteId, category}) => ({
        url: `/site-assignments/${regionId}/${siteId}/${category}/delivery-site`,
        method: "GET"
      }),
      providesTags: (result, error, arg) =>
        result
          ? [
            {type: "SiteAssignments", id: result.id},
            {type: "DeliverySiteSiteAssignments", id: result.siteId + "~" + result.category}
          ]
          : [
            {type: "DeliverySiteSiteAssignments", id: arg.siteId + "~" + arg.category}
          ]
    }),
    createSiteAssignment: builder.mutation<
      SiteAssignment,
      SiteAssignmentEntity
    >({
      query: (assignment) => ({
        url: "/site-assignments",
        method: "POST",
        body: assignment.asTypeValue()
      }),
      invalidatesTags: (result, error, arg) => {
        const tags : { type: TagType, id?: string }[] = []

        if (result) {
          tags.push({type: "SiteAssignments", id: "LIST"})
          if (result.type === siteAssignmentTypes.pickupSite.code) {
            tags.push({type: "PickupSiteSiteAssignments", id: result.siteId + "~" + result.category})
          } else if (result.type === siteAssignmentTypes.deliverySite.code) {
            tags.push({type: "DeliverySiteSiteAssignments", id: result.siteId + "~" + result.category})
          } else if (result.type === siteAssignmentTypes.company.code) {
            tags.push({type: "CompanySiteAssignments", id: result.siteId + "~" + result.category})
          }
        }

        return tags
      }
    }),
    createBulkSiteAssignment: builder.mutation<SiteAssignment[], SiteAssignmentTemplate>({
      query: (template) => ({
        url: '/site-assignments/bulk',
        method: 'POST',
        body: template,
      }),
      invalidatesTags: (result, _error, _arg) => {
        if (result) {
          const tags: any[] = [{type: 'SiteAssignments', id: 'LIST'}]
          for (const assignment of result) {
            tags.push({
              type: 'SiteSiteAssignments', id: assignment.siteId + "~" + assignment.category
            })
          }
          return tags
        } else {
          return [];
        }
      }
    }),
    updateSiteAssignment: builder.mutation<
      SiteAssignment,
      SiteAssignmentEntity
    >({
      query: (assignment) => ({
        url: "/site-assignments",
        method: "PUT",
        body: assignment.asTypeValue()
      }),
      invalidatesTags: (result, error, arg) => {
        const tags : { type: TagType, id?: string }[] = []

        if (result) {
          tags.push({type: "SiteAssignments", id: "LIST"})
          if (result.type === siteAssignmentTypes.pickupSite.code) {
            tags.push({type: "PickupSiteSiteAssignments", id: result.siteId + "~" + result.category})
          } else if (result.type === siteAssignmentTypes.deliverySite.code) {
            tags.push({type: "DeliverySiteSiteAssignments", id: result.siteId + "~" + result.category})
          } else if (result.type === siteAssignmentTypes.company.code) {
            tags.push({type: "CompanySiteAssignments", id: result.siteId + "~" + result.category})
          }
        }

        return tags
      }
    }),
    deleteSiteAssignment: builder.mutation<void, SiteAssignmentEntity>({
      query: (assignment) => ({
        url: `/site-assignments/${assignment.id}`,
        method: "DELETE",
        body: assignment.asTypeValue()
      }),
      invalidatesTags: (result, error, arg) => {
        const tags : { type: TagType, id?: string }[] = []

        if (!error) {
          tags.push({type: "SiteAssignments", id: "LIST"})
          if (arg.type.code === siteAssignmentTypes.pickupSite.code) {
            tags.push({type: "PickupSiteSiteAssignments", id: arg.siteId + "~" + arg.category.code})
          } else if (arg.type.code === siteAssignmentTypes.deliverySite.code) {
            tags.push({type: "DeliverySiteSiteAssignments", id: arg.siteId + "~" + arg.category.code})
          } else if (arg.type.code === siteAssignmentTypes.company.code) {
            tags.push({type: "CompanySiteAssignments", id: arg.siteId + "~" + arg.category.code})
          }
        }

        return tags
      }
    }),
    configureRouteSiteAssignments: builder.mutation<
      void,
      { routeId: string; assignment: SiteAssignmentEntity }
    >({
      query: ({routeId, assignment}) => ({
        url: "/site-assignments/route",
        method: "PUT",
        body: {
          routeId: routeId,
          assignmentTemplate: assignment.asTypeValue()
        }
      }),
      invalidatesTags: [
        {type: "SiteAssignments", id: "LIST"},
        {type: "PickupSiteSiteAssignments"},
        {type: "DeliverySiteSiteAssignments"},
        {type: "CompanySiteAssignments"}
      ]
    })
  })
});

export const {
  useGetAllSiteAssignmentsQuery,
  useGetSiteAssignmentQuery,
  useGetPickupSiteSiteAssignmentBySiteQuery,
  useLazyGetPickupSiteSiteAssignmentBySiteQuery,
  useGetDeliverySiteSiteAssignmentBySiteQuery,
  useLazyGetDeliverySiteSiteAssignmentBySiteQuery,
  useGetCompanySiteAssignmentBySiteQuery,
  useLazyGetCompanySiteAssignmentBySiteQuery,
  useCreateSiteAssignmentMutation,
  useCreateBulkSiteAssignmentMutation,
  useUpdateSiteAssignmentMutation,
  useDeleteSiteAssignmentMutation,
  useConfigureRouteSiteAssignmentsMutation
} = siteAssignmentApi;
